import { cn } from '@nextui-org/react'
import React, { useCallback, useRef } from 'react'

export interface NeoSwitchProps {
  /** Whether the switch is currently checked */
  checked: boolean
  /** Callback function called when the switch state changes */
  onCheckedChange: (checked: boolean) => void
  /** Whether the switch is disabled */
  disabled?: boolean
  /** Additional CSS class names to apply to the switch */
  className?: string
}

/**
 * NeoSwitch - Switch component matching Figma design specifications exactly
 * (No external dependencies, custom implementation)
 *
 * Figma Structure (from Content container specs):
 * - Track: 24px × 14px, 16px border-radius, 2px padding
 * - Padding creates usable area: 20px × 10px
 * - Handle: 10px × 10px circular (white rect with rx="5")
 * - Shadow: 0px 2px 4px rgba(0, 35, 11, 0.2)
 * - Colors: #262626 (on), rgba(0,0,0,0.12) (off)
 * - Animation: 200ms, translateX(10px) for ON state
 *
 * Measurements:
 * - Total: 24px wide × 14px tall
 * - Padding: 2px all sides
 * - Usable: 20px × 10px
 * - Handle: 10px × 10px (fits perfectly in usable area)
 * - Travel: 10px (20px usable width - 10px handle)
 */
export function NeoSwitch({ checked, onCheckedChange, disabled, className }: NeoSwitchProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)

  const handleToggle = useCallback(() => {
    if (!disabled) {
      onCheckedChange(!checked)
    }
  }, [checked, disabled, onCheckedChange])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault()
        handleToggle()
      }
    },
    [handleToggle]
  )

  return (
    <button
      ref={buttonRef}
      type='button'
      role='switch'
      aria-checked={checked}
      disabled={disabled}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      className={cn(
        // Track: 24px × 14px (correct Figma height!)
        'relative inline-flex h-[14px] w-[24px] shrink-0 items-center',
        // Track styling with 2px padding and 16px border-radius
        'rounded-[16px] p-[2px] transition-colors duration-200',
        // Focus states
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#262626]',
        // Cursor states
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        // State-based colors
        checked ? 'bg-[#262626]' : 'bg-[rgba(0,0,0,0.12)]',
        className
      )}
    >
      <span
        className={cn(
          // Handle: 10px × 10px circular (SVG rect with rx="5")
          'pointer-events-none block h-[10px] w-[10px] rounded-[16px]',
          // Handle styling with shadow matching Figma filter
          'bg-white shadow-[0px_2px_4px_0px_rgba(0,35,11,0.2)]',
          // Animation: smooth 200ms transition
          'transition-transform duration-200 ease-in-out',
          // Position: translateX(0) for OFF, translateX(10px) for ON
          // Travel distance: 20px usable width - 10px handle = 10px
          checked ? 'translate-x-[10px]' : 'translate-x-0'
        )}
      />
    </button>
  )
}
